import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getQuotation, 
  updateQuotation,
  updateQuotationFees, 
  saveQuotationNote,
  requestQuotationAdditionalInfo,
  listQuotationMessages,
  sendQuotationMessage,
  approveQuotation,
  archiveQuotation,
  updateQuotationStatus,
  markQuotationAsReviewed,
  sendQuotationBackToReviewer,
  addQuotationNote,
  listQuotationDocuments,
  createQuotationDocument
} from '../../services/quotations';
import { uploadFileAndGetUrl } from '../../services/coa';
import { generateInvoice, sendInvoice, getInvoicePDF, syncInvoice, getInvoice } from '../../services/invoices';
import SendBackToReviewerModal from '../../components/ui/SendBackToReviewerModal';
import AddSupportDocumentModal from '../../components/ui/AddSupportDocumentModal';
import toast from 'react-hot-toast';
import { formatTranslationsRequestedForDisplay } from '../../utils/formatTranslationSelectionLabel';
import {
  ADDITIONAL_INFO_REQUEST_TYPE_OPTIONS,
  DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE
} from '../../constants/additionalInfoRequestType';

const cleanHTML = (htmlString) => {
  if (!htmlString) return '';
  return String(htmlString).replace(/\r/g, '').trim();
};

const renderRichText = (content, className = 'text-base text-[#73827c]') => {
  const value = cleanHTML(content);
  if (!value) {
    return <span className={className}> </span>;
  }

  const looksLikeHtml = value.includes('<') && value.includes('>');
  if (looksLikeHtml) {
    return (
      <div
        className={`prose max-w-none ${className} [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800`}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }

  return (
    <div className={`whitespace-pre-wrap ${className}`}>
      {value}
    </div>
  );
};

const QuotationDetailRow = ({ label, value, hint }) => (
  <div className="flex gap-4 items-start py-3 border-b border-[#ebebeb] last:border-b-0">
    <div className="flex-1 min-w-0">
      <p className="text-base font-medium text-[#485b54]">{label}</p>
      {hint ? <p className="text-xs text-[#73827c] mt-1">{hint}</p> : null}
    </div>
    <p className="flex-1 text-base text-[#73827c] text-right whitespace-pre-wrap break-words">
      {value === undefined || value === null || value === '' ? 'N/A' : value}
    </p>
  </div>
);

const QuotationDetail = ({ quotationId, quotation: initialQuotation, onBack }) => {
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(initialQuotation);
  const [loading, setLoading] = useState(!initialQuotation);
  const [isCollapsed, setIsCollapsed] = useState({
    licenceSelection: false,
    translations: false,
    supportDocuments: false,
    organisationDetails: false,
    contactDetails: false,
    additionalRequirements: false
  });
  const [activeTab, setActiveTab] = useState('Applicants');
  const [messageText, setMessageText] = useState('');
  const [note, setNote] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState({
    message: '',
    // OLD (kept for revert): requestType: 'Licence Awaiting Review',
    requestType: DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE,
    dueDate: '',
    attachment: null
  });
  const [sendingRequest, setSendingRequest] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isDraggingEcoa, setIsDraggingEcoa] = useState(false);
  const [fees, setFees] = useState({
    licenceFee: '',
    translationFee: '',
    supportDocsFee: ''
  });
  const [messages, setMessages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRoles, setCurrentUserRoles] = useState([]);
  const noteSaveTimeoutRef = useRef(null);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  useEffect(() => {
    if (quotationId && !initialQuotation) {
      loadQuotation();
    } else if (initialQuotation) {
      setNote(initialQuotation.note || '');
    }
  }, [quotationId, initialQuotation]);

  useEffect(() => {
    if (quotation?._id) {
      loadMessages();
    }
  }, [quotation?._id, activeTab]);

  useEffect(() => {
    // Debounce note saving
    if (noteSaveTimeoutRef.current) {
      clearTimeout(noteSaveTimeoutRef.current);
    }
    
    if (quotation?._id && note !== undefined) {
      noteSaveTimeoutRef.current = setTimeout(() => {
        handleSaveNote();
      }, 1000); // Save after 1 second of no typing
    }

    return () => {
      if (noteSaveTimeoutRef.current) {
        clearTimeout(noteSaveTimeoutRef.current);
      }
    };
  }, [note]);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const response = await getQuotation(quotationId);
      if (response && response.success !== false) {
        const quotationData = response.data || response;
        setQuotation(quotationData);
        // Initialize fees from API – use stored breakdown, or derive from feeItems when stored values are 0
        if (quotationData.fees) {
          const f = quotationData.fees;
          const items = Array.isArray(f.feeItems) ? f.feeItems : [];
          const sumByType = (type) => items
            .filter((item) => item.type === type)
            .reduce((sum, item) => sum + (Number(item.amount) || 0) * (Number(item.qty) || 1), 0);
          const derivedLicence = sumByType('licence');
          const derivedTranslation = sumByType('translation');
          const derivedSupport = sumByType('supportDoc');
          setFees({
            licenceFee: (f.licenceFee != null && f.licenceFee !== '') ? String(f.licenceFee) : (derivedLicence ? String(derivedLicence) : ''),
            translationFee: (f.translationFeeTotal != null && f.translationFeeTotal !== '') ? String(f.translationFeeTotal) : (derivedTranslation ? String(derivedTranslation) : ''),
            supportDocsFee: (f.supportDocsFeeTotal != null && f.supportDocsFeeTotal !== '') ? String(f.supportDocsFeeTotal) : (derivedSupport ? String(derivedSupport) : '')
          });
        }
        // Initialize note (legacy single note) and notes array is in quotationData.notes
        setNote(quotationData.note || '');
        
        // Load timeline from quotation data
        setTimeline(quotationData.timeline || []);
        
        // Load invoice if exists
        if (quotationData.invoiceId) {
          try {
            const invoiceRes = await getInvoice(quotationData.invoiceId);
            if (invoiceRes && invoiceRes.success !== false) {
              setInvoice(invoiceRes.data || invoiceRes);
            }
          } catch (err) {
            console.error('Failed to load invoice:', err);
          }
        }
      } else {
        toast.error('Failed to load quotation details');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load quotation details');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!quotation?._id) return;
    try {
      setLoadingMessages(true);
      const messageType = activeTab === 'Staff' ? 'internal' : 'customer';
      const id = typeof quotation._id === 'string' ? quotation._id : (quotation._id?.toString?.() || quotation._id);
      const messagesRes = await listQuotationMessages(id, { type: messageType, limit: 100 });
      
      if (messagesRes && messagesRes.success !== false) {
        const data = messagesRes.data || messagesRes;
        const list = Array.isArray(data.messages) ? data.messages : (data.data?.messages || []);
        setMessages(list);
      }
      
      // Timeline is already loaded from quotation data in loadQuotation
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const toggleCollapse = (section) => {
    setIsCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveNote = async () => {
    if (!quotation?._id) return;
    
    try {
      await saveQuotationNote(quotation._id, note);
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !quotation?._id) return;
    try {
      setAddingNote(true);
      const response = await addQuotationNote(quotation._id, newNote.trim());
      if (response?.success && response?.data?.note) {
        setQuotation(prev => ({
          ...prev,
          notes: Array.isArray(prev.notes) ? [...prev.notes, response.data.note] : [response.data.note]
        }));
        setNewNote('');
        toast.success('Note added');
      } else {
        toast.error(response?.error || 'Failed to add note');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddQuotationDocument = async (newDoc) => {
    if (!quotation?._id) return;
    try {
      setUploadingDocument(true);
      let fileUrl = newDoc.fileUrl;
      if (newDoc.file && !fileUrl) {
        fileUrl = await uploadFileAndGetUrl(newDoc.file);
      }
      const feeValue = newDoc.fee === '£0.00' || !newDoc.fee ? 0 : parseFloat(String(newDoc.fee).replace(/£/g, '').replace(/,/g, '')) || 0;
      await createQuotationDocument(quotation._id, {
        name: newDoc.name,
        detail: newDoc.detail || '',
        fee: feeValue,
        fileUrl,
        isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : true
      });
      toast.success('Document added');
      setIsAddDocumentModalOpen(false);
      await loadQuotation();
    } catch (err) {
      console.error('Failed to add document:', err);
      toast.error(err.message || 'Failed to add document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !quotation?._id) {
      return;
    }
    
    try {
      setSendingMessage(true);
      const payload = {
        body: messageText.trim(),
        toCustomerId: quotation.customerId?._id || quotation.customerId,
        isInternal: activeTab === 'Staff',
        messageType: 'text'
      };
      
      const id = typeof quotation._id === 'string' ? quotation._id : (quotation._id?.toString?.() || quotation._id);
      const response = await sendQuotationMessage(id, payload);
      if (response && response.success !== false) {
        toast.success('Message sent!');
        setMessageText('');
        await loadMessages(); // Reload messages so the new one appears
      } else {
        toast.error(response?.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;
    
    try {
      setUploadingFile(true);
      const fileUrl = await uploadFileAndGetUrl(file);
      return fileUrl;
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('Failed to upload file. Please try again.');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDragEnter = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'ecoa') {
      setIsDraggingEcoa(true);
    } else {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'ecoa') {
      setIsDraggingEcoa(false);
    } else {
      setIsDraggingFile(false);
    }
  };

  const handleFileDrop = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'ecoa') {
      setIsDraggingEcoa(false);
      // Handle eCOA file drop if needed
    } else {
      setIsDraggingFile(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        setAdditionalInfo({ ...additionalInfo, attachment: file });
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAdditionalInfo({ ...additionalInfo, attachment: file });
    }
  };

  const handleSendRequest = async () => {
    if (!additionalInfo.message.trim()) {
      toast.error('Please enter a message to the applicant');
      return;
    }
    
    if (!quotation || !quotation._id) {
      toast.error('Quotation information not available');
      return;
    }
    
    try {
      setSendingRequest(true);
      
      // Upload attachment if provided
      let attachmentUrl = null;
      let attachmentFilename = null;
      
      if (additionalInfo.attachment) {
        const uploadedUrl = await handleFileUpload(additionalInfo.attachment);
        if (uploadedUrl) {
          attachmentUrl = uploadedUrl;
          attachmentFilename = additionalInfo.attachment.name;
        } else {
          toast.error('File upload failed. Please try again.');
          setSendingRequest(false);
          setUploadingFile(false);
          return;
        }
      }
      
      const payload = {
        message: additionalInfo.message.trim(),
        requestType: additionalInfo.requestType,
        dueDate: additionalInfo.dueDate || undefined,
        attachmentUrl: attachmentUrl || undefined,
        attachmentFilename: attachmentFilename || undefined
      };
      
      const response = await requestQuotationAdditionalInfo(quotation._id, payload);
      if (response && response.success !== false) {
        toast.success('Additional information request sent successfully!');
        
        // Reset form
        setAdditionalInfo({
          message: '',
          // OLD (kept for revert): requestType: 'Licence Awaiting Review',
          requestType: DEFAULT_ADDITIONAL_INFO_REQUEST_TYPE,
          dueDate: '',
          attachment: null
        });
        
        // Reload quotation to update timeline
        loadQuotation();
        // Reload messages to show the request
        loadMessages();
      } else {
        toast.error(response?.message || 'Failed to send request');
      }
    } catch (err) {
      console.error('Failed to send additional info request:', err);
      toast.error(err.message || 'Failed to send request. Please try again.');
    } finally {
      setSendingRequest(false);
      setUploadingFile(false);
    }
  };

  const handleApproveQuotation = async () => {
    if (!quotation?._id) return;
    
    try {
      setUpdatingStatus(true);
      const response = await approveQuotation(quotation._id);
      if (response && response.success !== false) {
        toast.success('Quotation approved and Ready for Signature!');
        await loadQuotation();
      } else {
        toast.error(response?.message || 'Failed to approve quotation');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to approve quotation');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkAsReviewed = async () => {
    if (!quotation?._id) return;
    
    try {
      setUpdatingStatus(true);
      const response = await markQuotationAsReviewed(quotation._id);
      if (response && response.success !== false) {
        toast.success('Quotation marked as reviewed. Approver has been notified.');
        await loadQuotation();
      } else {
        toast.error(response?.error || 'Failed to mark as reviewed');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to mark as reviewed');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendBackToReviewer = async (data) => {
    try {
      setUpdatingStatus(true);
      await sendQuotationBackToReviewer(quotation._id || quotationId, data);
      await loadQuotation();
      toast.success('Quotation sent back to reviewer. Reviewer has been notified.');
      setShowSendBackModal(false);
    } catch (err) {
      console.error('Failed to send back to reviewer:', err);
      toast.error(err?.message || 'Failed to send back to reviewer. Please try again.');
      throw err;
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleArchiveQuotation = async () => {
    if (!quotation?._id) return;
    
    try {
      const response = await archiveQuotation(quotation._id);
      if (response && response.success !== false) {
        toast.success('Quotation archived successfully!');
        loadQuotation(); // Reload to update status
      } else {
        toast.error(response?.message || 'Failed to archive quotation');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to archive quotation');
    }
  };

  const handleUpdateFees = async () => {
    try {
      const feesData = {
        licenceFee: parseFloat(fees.licenceFee) || 0,
        translationFeeTotal: parseFloat(fees.translationFee) || 0,
        supportDocsFeeTotal: parseFloat(fees.supportDocsFee) || 0,
        totalPrice: (parseFloat(fees.licenceFee) || 0) + 
                   (parseFloat(fees.translationFee) || 0) + 
                   (parseFloat(fees.supportDocsFee) || 0)
      };
      await updateQuotationFees(quotation._id || quotationId, feesData);
      toast.success('Fees updated successfully');
      loadQuotation();
    } catch (err) {
      toast.error(err.message || 'Failed to update fees');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#40c191] mx-auto"></div>
          <p className="mt-4 text-[#485b54]">Loading quotation details...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500">Quotation not found</p>
          <button onClick={onBack} className="mt-4 px-4 py-2 bg-[#40c191] text-white rounded-lg">
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  const coa = quotation.coaId || {};
  const coaTranslations = quotation.coaTranslations || (Array.isArray(coa.translations) ? coa.translations : (quotation.translations || []));
  const territoryLabel = Array.isArray(quotation.territory)
    ? quotation.territory.join(', ')
    : quotation.territory || 'N/A';
  const translationsRequestedLabel = (() => {
    if (quotation.wantOriginalTranslation) return 'Source language version';
    const requested = quotation.translationsRequested;
    if (!Array.isArray(requested) || requested.length === 0) return 'None';
    return formatTranslationsRequestedForDisplay(requested, coaTranslations) || 'None';
  })();
  const availableTranslationsCount = Array.isArray(coaTranslations) ? coaTranslations.length : 0;
  // Same fee flow and calculations as licence request details: use quotation.fees, format as £X.00
  const qFees = quotation.fees || {};
  const licenceFeeVal = Number(qFees.licenceFee) || parseFloat(fees.licenceFee) || 0;
  const translationFeeVal = Number(qFees.translationFeeTotal) || parseFloat(fees.translationFee) || 0;
  const supportFeeVal = Number(qFees.supportDocsFeeTotal) || parseFloat(fees.supportDocsFee) || 0;
  const ecoaFeeVal = Number(qFees.ecoaFeeTotal) || 0;
  const otherFeesVal = Number(qFees.otherFeesTotal) || 0;
  const totalPrice = Number(qFees.totalPrice) || (licenceFeeVal + translationFeeVal + supportFeeVal + ecoaFeeVal + otherFeesVal);
  const translationCount = Array.isArray(quotation.translations) ? quotation.translations.length : (Array.isArray(quotation.translationsRequested) ? quotation.translationsRequested.length : 0);
  const formattedFeesQuotation = {
    fee: licenceFeeVal ? `£${licenceFeeVal.toFixed(2)}` : '£0.00',
    translationFee: translationFeeVal ? `£${translationFeeVal.toFixed(2)}` : '£0.00',
    supportFee: supportFeeVal ? `£${supportFeeVal.toFixed(2)}` : '£0.00',
    ecoaFee: ecoaFeeVal ? `£${ecoaFeeVal.toFixed(2)}` : '£0.00',
    otherFee: otherFeesVal ? `£${otherFeesVal.toFixed(2)}` : '£0.00',
    totalPrice: totalPrice ? `£${totalPrice.toFixed(2)}` : '£0.00'
  };

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <div className="px-6 py-4">
        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Change Note Alert - Only show for reviewer when note exists */}
            {quotation?.changeNote && 
             quotation?.reviewerId && 
             currentUserId && 
             quotation.reviewerId._id?.toString() === currentUserId && 
             (quotation.status === 'Under Review' || quotation.status === 'Pending') && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      Request Sent Back for Review
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="whitespace-pre-wrap bg-red-100 p-3 rounded border-2 border-red-400">
                        {quotation.changeNote}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Back Button */}
            <button 
              onClick={onBack} 
              className="flex items-center gap-2 text-[#73827c] hover:text-[#222f2a] transition-colors mb-2"
            >
              <svg width="11" height="11" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 5L7.5 10L12.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>

            {/* Page Title + Edit (same as licence request details) */}
            <div className="flex items-center justify-between gap-4">
              <h1 className="text-[32px] font-semibold text-[#212529] tracking-[-0.64px]">
                Quotation
              </h1>
              {/* <button
                type="button"
                onClick={() => navigate(`/edit-quotation/${quotationId || quotation?._id}`)}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                Edit
              </button> */}
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-4">
              {/* Licence Selection */}
              <div className="bg-white border border-[#f2f2f2] px-[10px] py-[5px] rounded-2xl">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('licenceSelection')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.6667 2.5H3.33333C2.41286 2.5 1.66667 3.24619 1.66667 4.16667V15.8333C1.66667 16.7538 2.41286 17.5 3.33333 17.5H16.6667C17.5871 17.5 18.3333 16.7538 18.3333 15.8333V4.16667C18.3333 3.24619 17.5871 2.5 16.6667 2.5Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3333 5.83333H6.66667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3333 10H6.66667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 14.1667H6.66667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]">Licence Selection</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.licenceSelection ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.licenceSelection && (
                  <div className="px-4 pb-4 space-y-0">
                    <QuotationDetailRow label="COA Name:" value={coa.name || 'N/A'} />
                    <QuotationDetailRow label="Organisation Type:" value={quotation.organizationType} />
                    <QuotationDetailRow
                      label="Intended Use:"
                      value={quotation.intendedUse}
                      hint="How the applicant intends to use the questionnaire in their research/study"
                    />
                    <QuotationDetailRow
                      label="Requested Translations:"
                      value={translationsRequestedLabel}
                      hint="Translations selected from the COA library or currently in development"
                    />
                    {quotation.customTranslationsText ? (
                      <QuotationDetailRow
                        label="Custom New Translation:"
                        value={quotation.customTranslationsText}
                        hint="Translations the customer wants to develop or commission"
                      />
                    ) : null}
                    {quotation.translationTerritory ? (
                      <QuotationDetailRow
                        label="Translation Territory:"
                        value={quotation.translationTerritory}
                        hint="The territory where the translation will be used"
                      />
                    ) : null}
                    <QuotationDetailRow
                      label="Original Source Version Requested:"
                      value={quotation.wantOriginalTranslation ? 'Yes' : 'No'}
                      hint="Whether the customer requested the original developer questionnaire"
                    />
                    <QuotationDetailRow
                      label="Territories / Regions:"
                      value={territoryLabel}
                      hint="Geographic regions where the COA will be deployed"
                    />
                    <QuotationDetailRow
                      label="eCOA Migration Requested:"
                      value={quotation.eCOA ? 'Yes' : 'No'}
                      hint="Whether the customer asked for permission to digitally migrate the instrument"
                    />
                    <QuotationDetailRow
                      label="Start Date:"
                      value={formatDate(quotation.startDate)}
                      hint="Planned start date for use"
                    />
                    <QuotationDetailRow
                      label="End Date:"
                      value={formatDate(quotation.endDate)}
                      hint="Planned end date for use"
                    />
                    <QuotationDetailRow
                      label="Number of Users:"
                      value={quotation.numUsers}
                      hint="Total number of users who will access the instrument"
                    />
                    <QuotationDetailRow
                      label="Number of Uses:"
                      value={quotation.numUses}
                      hint="Number of times each user will complete the questionnaire"
                    />
                    <QuotationDetailRow label="PO / Trial Number:" value={quotation.pqTrustNumber} />
                  </div>
                )}
              </div>

              {/* Translations */}
              <div className="bg-white border border-[#f2f2f2] rounded-2xl px-[10px] py-[5px]">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('translations')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 1.66667L12.575 7.15833L18.3333 7.725L14.1667 11.5917L15.15 17.3083L10 14.4917L4.85 17.3083L5.83333 11.5917L1.66667 7.725L7.425 7.15833L10 1.66667Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]">Translations</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.translations ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.translations && (
                  <div className="px-4 pb-4 space-y-0">
                    <QuotationDetailRow
                      label="Available Translations:"
                      value={`${availableTranslationsCount} in COA library`}
                    />
                    <QuotationDetailRow
                      label="Requested Translations:"
                      value={translationsRequestedLabel}
                    />
                    <QuotationDetailRow
                      label="Custom Translations:"
                      value={quotation.customTranslationsText || 'N/A'}
                    />
                    <QuotationDetailRow label="Territories:" value={territoryLabel} />
                  </div>
                )}
              </div>

              {/* Support Documents */}
              <div className="bg-white border border-[#f2f2f2] rounded-2xl px-[10px] py-[5px]">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('supportDocuments')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33333 2.89131 3.33333 3.33333V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M11.6667 1.66667V6.66667H16.6667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3333 10.8333H6.66667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3333 14.1667H6.66667" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]">Support Documents</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.supportDocuments ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.supportDocuments && (
                  <div className="px-4 pb-4">
                    <div className="flex flex-wrap gap-2">
                      {quotation.supportDocuments.map((doc, index) => {
                        const fee = doc.fee ?? doc.amount;
                        const detail = doc.detail || doc.description || 'Support document';
                        return (
                          <div key={doc._id || index} className="w-40 flex-none bg-[#fafafa] border border-[#ebebeb] rounded-lg p-2">
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 shrink-0 bg-gray-200 rounded flex items-center justify-center">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14 2V8H20" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-medium text-[#485b54] truncate" title={doc.name}>{doc.name || `Document ${index + 1}`}</h4>
                                <p className="text-xs text-[#73827c] truncate" title={detail}>{detail.length > 20 ? detail.slice(0, 20) + '…' : detail}</p>
                                <span className="text-xs text-[#485b54]">£{fee != null ? Number(fee).toFixed(2) : '0.00'}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* <div className="flex justify-between py-3 border-b border-[#ebebeb]">
                      <span className="text-base font-medium text-[#485b54]">Additional Requirements</span>
                      <span className="text-base text-[#73827c]">{quotation.terms?.restrictions || ' '}</span>
                    </div> */}
                  </div>
                )}
              </div>

              {/* Organisation Details */}
              <div className="bg-white border border-[#f2f2f2] rounded-2xl px-[10px] py-[5px]">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('organisationDetails')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0653 12.8512 14.2174 12.5 13.3333 12.5H6.66667C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9.16667C11.841 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.841 2.5 10 2.5C8.15905 2.5 6.66667 3.99238 6.66667 5.83333C6.66667 7.67428 8.15905 9.16667 10 9.16667Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]">Organisation Details</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.organisationDetails ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.organisationDetails && (
                  <div className="px-4 pb-4 space-y-0">
                    <QuotationDetailRow label="Organisation Name:" value={quotation.org?.name} />
                    <QuotationDetailRow label="Address 1:" value={quotation.org?.address1} />
                    <QuotationDetailRow label="Address 2:" value={quotation.org?.address2} />
                    <QuotationDetailRow label="City:" value={quotation.org?.city} />
                    <QuotationDetailRow label="Region:" value={quotation.org?.region || quotation.org?.state} />
                    <QuotationDetailRow label="Postcode:" value={quotation.org?.postcode} />
                    <QuotationDetailRow label="Country:" value={quotation.org?.country} />
                    <QuotationDetailRow label="VAT Number:" value={quotation.org?.vatNumber} />
                  </div>
                )}
              </div>

              {/* Contact Details */}
              <div className="bg-white border border-[#f2f2f2] rounded-2xl px-[10px] py-[5px]">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('contactDetails')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0653 12.8512 14.2174 12.5 13.3333 12.5H6.66667C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9.16667C11.841 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.841 2.5 10 2.5C8.15905 2.5 6.66667 3.99238 6.66667 5.83333C6.66667 7.67428 8.15905 9.16667 10 9.16667Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]">Contact Details</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.contactDetails ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.contactDetails && (
                  <div className="px-4 pb-4 space-y-0">
                    <QuotationDetailRow label="Full Name:" value={quotation.contact?.fullName} />
                    <QuotationDetailRow label="Job Title:" value={quotation.contact?.jobTitle} />
                    <QuotationDetailRow label="Email:" value={quotation.contact?.email} />
                    <QuotationDetailRow label="Phone:" value={quotation.contact?.phone} />
                  </div>
                )}
              </div>

              {/* Licensing Restrictions */}
              <div className="bg-white border border-[#f2f2f2] rounded-2xl px-[10px] py-[5px]">
                <div 
                  className="flex items-center justify-between px-4 py-4 cursor-pointer"
                  onClick={() => toggleCollapse('additionalRequirements')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-[34px] h-[34px] bg-[#f0fff2] rounded-lg flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.6667 2.5H3.33333C2.41286 2.5 1.66667 3.24619 1.66667 4.16667V15.8333C1.66667 16.7538 2.41286 17.5 3.33333 17.5H16.6667C17.5871 17.5 18.3333 16.7538 18.3333 15.8333V4.16667C18.3333 3.24619 17.5871 2.5 16.6667 2.5Z" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.66667 5.83333H13.3333" stroke="#41c291" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#222f2a]"> Licensing Restrictions</h3>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      className={`transition-transform ${isCollapsed.additionalRequirements ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9L12 15L18 9" stroke="#73827c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                {!isCollapsed.additionalRequirements && (
                  <div className="px-4 pb-4">
                    <div className="bg-white px-3 py-3 rounded-lg min-h-[172px]">
                      {renderRichText(
                        quotation.terms?.restrictions ||
                          quotation.terms?.termsContent ||
                          coa.restrictions,
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-[320px] space-y-[16px] flex-shrink-0">
            {/* Status Buttons */}
            <div className="flex gap-3 flex-wrap">
              <button 
                onClick={handleArchiveQuotation}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] rounded-[8px] text-[14px] text-[#485b54] bg-white hover:bg-gray-50 transition-colors"
              >
                Archived
              </button>
              <button
                type="button"
                onClick={() => navigate(`/edit-quotation/${quotationId || quotation?._id}`)}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                Edit
              </button>
              {/* Mark as Reviewed button - only show if user is reviewer and status is Under Review */}
              {quotation?.reviewerId && 
               currentUserId && 
               quotation.reviewerId._id?.toString() === currentUserId && 
               quotation.status === 'Under Review' && (
                <button
                  onClick={handleMarkAsReviewed}
                  disabled={updatingStatus}
                  className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#40C191] to-[#5ED58D] text-white rounded-[8px] text-[14px] font-medium hover:from-[#35A47C] hover:to-[#4EC57A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingStatus ? 'Updating...' : 'Mark as Reviewed'}
                </button>
              )}
              {/* Send Back to Reviewer button - show if user is approver and status is Reviewed or Ready for Signature */}
              {currentUserId && 
               (quotation?.status === 'Reviewed' || quotation?.status === 'Ready for Signature') &&
               (currentUserRoles.includes('Approver') || currentUserRoles.includes('SuperAdmin')) && (
                <button
                  onClick={() => setShowSendBackModal(true)}
                  disabled={updatingStatus}
                  className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] text-white rounded-[8px] text-[14px] font-medium hover:from-[#E55555] hover:to-[#FF7A7A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send Back to Reviewer
                </button>
              )}
              {/* Ready for Signature button - only show if user is approver and status is Reviewed */}
              {currentUserId && 
               quotation?.status === 'Reviewed' &&
               (currentUserRoles.includes('Approver') || currentUserRoles.includes('SuperAdmin')) && (
                <button 
                  onClick={handleApproveQuotation}
                  disabled={quotation?.status === 'Ready for Signature' || updatingStatus}
                  className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-[8px] text-[14px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  Ready for Signature
                </button>
              )}
              {/* Approve button - only show if user is approver and status is Terms & Conditions Signed */}
              {currentUserId && 
               quotation?.status === 'Terms & Conditions Signed' &&
               (currentUserRoles.includes('Approver') || currentUserRoles.includes('SuperAdmin')) && (
                <button 
                  onClick={async () => {
                    if (!quotation?._id) return;
                    try {
                      setUpdatingStatus(true);
                      await updateQuotationStatus(quotation._id, 'Approved', 'Quotation approved after Terms & Conditions signed');
                      toast.success('Quotation approved successfully!');
                      await loadQuotation();
                    } catch (err) {
                      toast.error('Failed to approve quotation: ' + (err.message || 'Unknown error'));
                    } finally {
                      setUpdatingStatus(false);
                    }
                  }}
                  disabled={updatingStatus}
                  className="px-6 py-2 h-[40px] bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-[8px] text-[14px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {updatingStatus ? 'Approving...' : 'Approve'}
                </button>
              )}
            </div>

            {/* Licence ID & Submitted */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <div className="space-y-0">
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">Licence ID</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">{quotation.refNumber || 'N/A'}</p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">Submitted:</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">{formatDate(quotation.submittedAt)}</p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-center py-[12px]">
                  <p className="font-medium text-[16px] text-[#485b54]">Status:</p>
                  <div className="flex-1 relative">
                    <select 
                      value={quotation?.status || 'Pending'}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        if (newStatus === quotation.status) return;
                        try {
                          setUpdatingStatus(true);
                          await updateQuotationStatus(quotation._id || quotationId, newStatus, '');
                          toast.success('Status updated successfully');
                          await loadQuotation();
                        } catch (err) {
                          toast.error('Failed to update status: ' + (err.message || 'Unknown error'));
                          e.target.value = quotation.status; // Reset on error
                        } finally {
                          setUpdatingStatus(false);
                        }
                      }}
                      disabled={updatingStatus}
                      className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Awaiting Review">Awaiting Review</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Reviewed">Reviewed</option>
                      <option value="Ready for Signature">Ready for Signature</option>
                      <option value="Terms & Conditions Signed">Terms & Conditions Signed</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Archived">Archived</option>
                    </select>
                    <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#73827c" strokeWidth="1.5">
                        <path d="M5 7.5L10 12.5L15 7.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
                {(quotation?.payment?.status && quotation.payment.status !== 'NotRequired') && (
                  <>
                    <div className="h-px bg-[#ebebeb]"></div>
                    <div className="flex gap-[16px] items-center py-[12px]">
                      <p className="font-medium text-[16px] text-[#485b54]">Payment:</p>
                      <div className="flex-1 relative">
                        <select 
                          value={quotation?.payment?.status || 'NotRequired'}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            const currentStatus = quotation?.payment?.status || 'NotRequired';
                            if (newStatus === currentStatus) return;
                            try {
                              setUpdatingPayment(true);
                              const response = await updateQuotation(quotation._id || quotationId, {
                                payment: { status: newStatus, updatedAt: new Date() }
                              });
                              if (response && response.success !== false) {
                                toast.success('Payment status updated successfully');
                                await loadQuotation();
                              } else {
                                throw new Error(response?.message || 'Failed to update payment status');
                              }
                            } catch (err) {
                              toast.error('Failed to update payment status: ' + (err.message || 'Unknown error'));
                              e.target.value = currentStatus;
                            } finally {
                              setUpdatingPayment(false);
                            }
                          }}
                          disabled={updatingPayment}
                          className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="NotRequired">Not Required</option>
                          <option value="Pending">Pending</option>
                          <option value="Completed">Completed</option>
                          <option value="Failed">Failed</option>
                        </select>
                        <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#73827c" strokeWidth="1.5">
                            <path d="M5 7.5L10 12.5L15 7.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes (same as licence request details) */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[8px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">Notes</h3>
              <div className="space-y-4">
                {(Array.isArray(quotation?.notes) ? quotation.notes : []).length === 0 ? (
                  <p className="text-sm text-[#73827c] py-2">No notes yet</p>
                ) : (
                  [...(Array.isArray(quotation?.notes) ? quotation.notes : [])]
                    .reverse()
                    .map((n, index) => (
                      <div key={index} className="border border-[#E5E7EB] rounded-lg p-4 bg-[#fafafa]">
                        <p className="text-sm text-[#485B54] mb-2 whitespace-pre-wrap">{n.note}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-[#222f2a]">
                            {n.addedBy?.fullName || 'Unknown User'}
                          </span>
                          <span className="font-semibold text-[#485b54]">
                            {n.addedAt ? new Date(n.addedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      </div>
                    ))
                )}
                <div className="pt-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={3}
                    className="w-full px-[12px] py-[12px] bg-white border border-[#f2f2f2] rounded-[8px] text-[14px] text-[#485b54] focus:outline-none focus:ring-2 focus:ring-[#40C191] resize-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="mt-2 px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </div>
            </div>

            {/* Support Documents (upload – same as licence request details) */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">Support Documents</h3>
              <div className="space-y-2 w-64 max-w-full">
                {(!quotation?.supportDocuments || quotation.supportDocuments.length === 0) ? (
                  <p className="text-[14px] text-[#73827c] py-2">No support documents uploaded</p>
                ) : (
                  quotation.supportDocuments.map((doc) => (
                    <div key={doc._id} className="bg-[#fafafa] border border-[#ebebeb] rounded-[12px] p-3 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#485b54] truncate">{doc.name}</p>
                        {doc.detail && <p className="text-xs text-[#73827c] truncate" title={doc.detail}>{doc.detail.length > 20 ? doc.detail.slice(0, 20) + '…' : doc.detail}</p>}
                      </div>
                      {(Number(doc.fee) || 0) > 0 ? (
                        <span className="text-sm text-[#485b54] shrink-0">£{Number(doc.fee).toFixed(2)}</span>
                      ) : (
                        <span className="text-sm text-[#73827c] shrink-0">Free</span>
                      )}
                      {doc.fileUrl && (
                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#40C191] hover:underline shrink-0">View</a>
                      )}
                    </div>
                  ))
                )}
                <button
                  type="button"
                  onClick={() => setIsAddDocumentModalOpen(true)}
                  disabled={uploadingDocument}
                  className="mt-2 px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50"
                >
                  {uploadingDocument ? 'Uploading…' : 'Upload support document'}
                </button>
              </div>
            </div>

            {/* Additional Info Required */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[24px] pb-[8px]">Additional Info Required</h3>
              <div className="space-y-[12px]">
                <div>
                  <label className="block text-[14px] text-[#485b54] mb-[4px]">Message to Applicant</label>
                  <textarea
                    value={additionalInfo.message}
                    onChange={(e) => setAdditionalInfo({ ...additionalInfo, message: e.target.value })}
                    className="w-full h-[96px] px-[12px] py-[12px] bg-white border border-[#f2f2f2] rounded-[8px] text-[14px] text-[#485b54] focus:outline-none focus:ring-2 focus:ring-[#40C191] resize-none"
                    placeholder=""
                  />
                </div>

                <div>
                  <label className="block text-[14px] text-[#485b54] mb-[4px]">Request Type</label>
                  <div className="relative">
                    <select 
                      value={additionalInfo.requestType}
                      onChange={(e) => setAdditionalInfo({ ...additionalInfo, requestType: e.target.value })}
                      className="w-full px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] appearance-none focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                    >
                      {/* OLD (kept for revert):
                      <option value="Licence Awaiting Review">Licence Awaiting Review</option>
                      <option value="More Information Required">More Information Required</option>
                      <option value="Pending Documentation">Pending Documentation</option>
                      <option value="Other">Other</option>
                      */}
                      {ADDITIONAL_INFO_REQUEST_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <div className="absolute right-[12px] top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#73827c" strokeWidth="1.5">
                        <path d="M5 7.5L10 12.5L15 7.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[14px] text-[#485b54] mb-[4px]">Due Date</label>
                  <input
                    type="date"
                    value={additionalInfo.dueDate}
                    onChange={(e) => setAdditionalInfo({ ...additionalInfo, dueDate: e.target.value })}
                    className="w-full px-[12px] py-[8px] bg-white border border-[#f2f2f2] rounded-[8px] text-[14px] text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191]"
                  />
                </div>

                <div>
                  <label className="block text-[14px] text-[#485b54] mb-[4px]">Attach Reference/Guide</label>
                  <div 
                    className={`border-2 border-dashed rounded-[8px] h-[57px] flex items-center justify-center cursor-pointer transition-colors relative ${
                      isDraggingFile
                        ? 'border-[#40c191] bg-[#f0fff2]'
                        : 'border-[#41c291] hover:bg-[#f0fff2]'
                    }`}
                    onDragEnter={(e) => handleDragEnter(e, 'file')}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={(e) => handleDragLeave(e, 'file')}
                    onDrop={(e) => handleFileDrop(e, 'file')}
                    onClick={() => document.getElementById('file-upload-input')?.click()}
                  >
                    <input
                      id="file-upload-input"
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.txt"
                    />
                    <div className="flex items-center gap-[8px]">
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#41c291" strokeWidth="1.5">
                        <path d="M11 1.83333V14.6667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7.33333 5.5L11 1.83333L14.6667 5.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.66667 14.6667V18.3333C3.66667 19.2538 4.41286 20 5.33333 20H16.6667C17.5871 20 18.3333 19.2538 18.3333 18.3333V14.6667" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p className="text-[14px] text-[#41c291]">
                        {additionalInfo.attachment ? additionalInfo.attachment.name : 'Drag & Drop'}
                      </p>
                    </div>
                  </div>
                  {additionalInfo.attachment && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAdditionalInfo({ ...additionalInfo, attachment: null });
                      }}
                      className="mt-2 text-[12px] text-red-500 hover:text-red-700"
                    >
                      Remove file
                    </button>
                  )}
                </div>

                <button
                  onClick={handleSendRequest}
                  disabled={sendingRequest || uploadingFile || !additionalInfo.message.trim()}
                  className="w-full px-[24px] py-[2px] h-[40px] bg-gradient-to-r from-[#40C191] to-[#5ED58D] text-white rounded-[8px] text-[14px] font-medium hover:from-[#35A47C] hover:to-[#4EC57A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingRequest ? 'Sending...' : uploadingFile ? 'Uploading...' : 'Send Request'}
                </button>
              </div>
            </div>

            {/* Fees – same flow, calculations and layout as licence request details (read-only) */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">Fees</h3>
              <div className="space-y-0">
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">Copyright licence fee:</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                    {formattedFeesQuotation.fee}
                  </p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                    Translation fee × {translationCount || 0}:
                  </p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                    {formattedFeesQuotation.translationFee}
                  </p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">Support documents:</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                    {formattedFeesQuotation.supportFee}
                  </p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">Other fees:</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                    {formattedFeesQuotation.otherFee}
                  </p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                <div className="flex gap-[16px] items-start py-[12px]">
                  <p className="flex-1 font-medium text-[16px] text-[#485b54]">eCOA fees:</p>
                  <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                    {formattedFeesQuotation.ecoaFee}
                  </p>
                </div>
                <div className="h-px bg-[#ebebeb]"></div>
                {qFees.vatApplicable && (qFees.vatAmount || 0) > 0 ? (
                  <>
                    <div className="flex gap-[16px] items-start py-[12px]">
                      <p className="flex-1 font-medium text-[16px] text-[#485b54]">Subtotal (ex. VAT):</p>
                      <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                        {formattedFeesQuotation.totalPrice}
                      </p>
                    </div>
                    <div className="h-px bg-[#ebebeb]"></div>
                    <div className="flex gap-[16px] items-start py-[12px]">
                      <p className="flex-1 font-medium text-[16px] text-[#485b54]">
                        VAT ({Math.round((qFees.vatRate || 0.2) * 100)}%):
                      </p>
                      <p className="flex-1 font-medium text-[16px] text-[#73827c] text-right">
                        £{(Number(qFees.vatAmount) || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="h-px bg-[#ebebeb]"></div>
                    <div className="flex gap-[16px] items-start py-[12px]">
                      <p className="flex-1 font-bold text-[16px] text-[#222f2a]">Total (inc. VAT):</p>
                      <p className="flex-1 font-bold text-[16px] text-[#222f2a] text-right">
                        £{(Number(qFees.grandTotal) || totalPrice + (Number(qFees.vatAmount) || 0)).toFixed(2)}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex gap-[16px] items-start py-[12px]">
                    <p className="flex-1 font-bold text-[16px] text-[#222f2a]">Total price:</p>
                    <p className="flex-1 font-bold text-[16px] text-[#222f2a] text-right">
                      {formattedFeesQuotation.totalPrice}
                    </p>
                  </div>
                )}
              </div>

              {/* When Approved, quotation becomes a licence request – link to it */}
              {quotation?.status === 'Approved' && quotation?.licenceId && (
                <div className="border-t border-[#ebebeb] mt-[12px] pt-[12px]">
                  <p className="font-medium text-[16px] text-[#485b54] mb-[8px]">Licence request</p>
                  <p className="text-[14px] text-[#73827c] mb-2">
                    This quotation has been converted to a licence request. It appears in Requested Licences and in the customer&apos;s My Licenses.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/licences/${quotation.licenceId._id || quotation.licenceId}`)}
                    className="text-[14px] font-medium text-[#40C191] hover:underline"
                  >
                    View licence request {quotation.licenceId?.refNumber || ''}
                  </button>
                </div>
              )}

              {/* Invoice Section */}
              {(quotation?.status === 'Terms & Conditions Signed' || quotation?.status === 'Approved') && (
                <div className="border-t border-[#ebebeb] mt-[12px] pt-[12px]">
                  <p className="font-medium text-[16px] text-[#485b54] mb-[8px]">Invoice</p>
                  {invoice ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[14px] text-[#73827c]">Invoice Number:</span>
                        <button
                          onClick={() => navigate(`/invoices/${invoice._id}`)}
                          className="text-[14px] text-[#40C191] hover:underline font-medium"
                        >
                          {invoice.invoiceNumber}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[14px] text-[#73827c]">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'PAID' ? 'bg-green-100 text-green-700' :
                          invoice.status === 'AUTHORISED' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={async () => {
                            try {
                              const blob = await getInvoicePDF(invoice._id);
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `invoice-${invoice.invoiceNumber}.pdf`;
                              link.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Invoice PDF downloaded');
                            } catch (err) {
                              toast.error('Failed to download invoice PDF');
                            }
                          }}
                          className="flex-1 px-3 py-2 text-[12px] font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Download PDF
                        </button>
                        {invoice.status !== 'PAID' && invoice.status !== 'REFUNDED' && invoice.status !== 'VOIDED' ? (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Send invoice to customer via email?')) return;
                              try {
                                setSendingInvoice(true);
                                const response = await sendInvoice(invoice._id);
                                if (response && response.success !== false) {
                                  toast.success('Invoice sent successfully!');
                                  await loadQuotation();
                                } else {
                                  toast.error(response?.error || 'Failed to send invoice');
                                }
                              } catch (err) {
                                toast.error('Failed to send invoice: ' + (err.message || 'Unknown error'));
                              } finally {
                                setSendingInvoice(false);
                              }
                            }}
                            disabled={sendingInvoice}
                            className="flex-1 px-3 py-2 text-[12px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                          >
                            {sendingInvoice ? 'Sending...' : 'Send Invoice'}
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Generate a new invoice for this quotation?')) return;
                              try {
                                setGeneratingInvoice(true);
                                const response = await generateInvoice(quotation._id || quotationId, 'quotation');
                                if (response && response.success !== false) {
                                  setInvoice(response.data || response);
                                  toast.success('Invoice generated successfully!');
                                  await loadQuotation();
                                } else {
                                  toast.error(response?.error || 'Failed to generate invoice');
                                }
                              } catch (err) {
                                toast.error('Failed to generate invoice: ' + (err.message || 'Unknown error'));
                              } finally {
                                setGeneratingInvoice(false);
                              }
                            }}
                            disabled={generatingInvoice}
                            className="flex-1 px-3 py-2 text-[12px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                          >
                            {generatingInvoice ? 'Generating...' : 'Generate New Invoice'}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Generate invoice for this quotation?')) return;
                        try {
                          setGeneratingInvoice(true);
                          const response = await generateInvoice(quotation._id || quotationId, 'quotation');
                          if (response && response.success !== false) {
                            setInvoice(response.data || response);
                            toast.success('Invoice generated successfully!');
                            await loadQuotation();
                          } else {
                            toast.error(response?.error || 'Failed to generate invoice');
                          }
                        } catch (err) {
                          toast.error('Failed to generate invoice: ' + (err.message || 'Unknown error'));
                        } finally {
                          setGeneratingInvoice(false);
                        }
                      }}
                      disabled={generatingInvoice}
                      className="w-full px-4 py-2 text-[14px] font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                    >
                      {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* eCOA */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">eCOA</h3>
              <div 
                className={`border-2 border-dashed rounded-[8px] h-[57px] flex items-center justify-center cursor-pointer transition-colors relative ${
                  isDraggingEcoa
                    ? 'border-[#40c191] bg-[#f0fff2]'
                    : 'border-[#41c291] hover:bg-[#f0fff2]'
                }`}
                onDragEnter={(e) => handleDragEnter(e, 'ecoa')}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => handleDragLeave(e, 'ecoa')}
                onDrop={(e) => handleFileDrop(e, 'ecoa')}
                onClick={() => document.getElementById('ecoa-upload-input')?.click()}
              >
                <input
                  id="ecoa-upload-input"
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt"
                />
                <div className="flex items-center gap-[8px]">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#41c291" strokeWidth="1.5">
                    <path d="M11 1.83333V14.6667" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.33333 5.5L11 1.83333L14.6667 5.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3.66667 14.6667V18.3333C3.66667 19.2538 4.41286 20 5.33333 20H16.6667C17.5871 20 18.3333 19.2538 18.3333 18.3333V14.6667" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <p className="text-[14px] text-[#41c291]">Drag & Drop</p>
                </div>
              </div>
            </div>

            {/* Support Document */}
            {/* <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">Support Document</h3>
              <div className="space-y-0">
                {quotation.fees?.feeItems?.filter(item => item.type === 'supportDoc').length > 0 ? (
                  quotation.fees.feeItems.filter(item => item.type === 'supportDoc').map((doc, index) => (
                    <div key={index} className="flex gap-[16px] items-start py-[12px] border-b border-[#ebebeb] last:border-0">
                      <p className="flex-1 font-medium text-[16px] text-[#485b54] truncate">{doc.name || `Document ${index + 1}`}</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[16px] text-[#485b54]">£{doc.amount?.toFixed(2) || '0.00'}</p>
                        <button className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1.33333V11.3333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4 7.33333L8 11.3333L12 7.33333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2.66667 14.6667H13.3333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[14px] text-[#73827c] py-4">No support documents</p>
                )}
              </div>
            </div> */}

            {/* Files */}
            {/* <div className="bg-white border border-[#f2f2f2] rounded-[12px] px-[12px] py-[16px]">
              <h3 className="text-[18px] font-normal text-[#222f2a] mb-[16px] pb-[8px]">Files</h3>
              <div className="space-y-0">
                {quotation.timeline?.filter(item => item.type === 'file').length > 0 ? (
                  quotation.timeline.filter(item => item.type === 'file').map((file, index) => (
                    <div key={index} className="flex gap-[16px] items-start py-[12px] border-b border-[#ebebeb] last:border-0">
                      <p className="flex-1 font-medium text-[16px] text-[#485b54] truncate">{file.title || `File ${index + 1}`}</p>
                      <div className="flex items-center gap-1">
                        <button className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1.33333V11.3333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4 7.33333L8 11.3333L12 7.33333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2.66667 14.6667H13.3333" stroke="#485b54" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4H14" stroke="#f56565" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6.66667 4V2.66667C6.66667 2.31305 6.80714 1.97391 7.05719 1.72386C7.30724 1.47381 7.64638 1.33333 8 1.33333C8.35362 1.33333 8.69276 1.47381 8.94281 1.72386C9.19286 1.97391 9.33333 2.31305 9.33333 2.66667V4M11.3333 4V13.3333C11.3333 13.687 11.1929 14.0261 10.9428 14.2761C10.6928 14.5262 10.3536 14.6667 10 14.6667H6C5.64638 14.6667 5.30724 14.5262 5.05719 14.2761C4.80714 14.0261 4.66667 13.687 4.66667 13.3333V4H11.3333Z" stroke="#f56565" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[14px] text-[#73827c] py-4">No files</p>
                )}
              </div>
            </div> */}

            {/* Messages */}
            <div className="bg-white border border-[#f2f2f2] rounded-[12px] h-[503px] flex flex-col">
              {/* Tabs */}
              <div className="bg-[#f2f2f2] p-[6px] rounded-t-[12px]">
                <div className="flex gap-0">
                  {['Applicants', 'Staff'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 px-[16px] py-[6px] text-[14px] font-medium rounded-[8px] transition-colors ${
                        activeTab === tab
                          ? 'bg-emerald-400 text-white'
                          : 'text-emerald-400 hover:bg-white'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages Content */}
              <div className="flex-1 overflow-y-auto p-[12px] space-y-[12px]">
                {(() => {
                  if (loadingMessages) {
                    return <div className="text-center text-xs text-gray-500 py-4">Loading messages...</div>;
                  }
                  
                  // Combine and sort all entries by date
                  const allEntries = [
                    ...timeline.map(entry => ({
                      type: 'timeline',
                      data: entry,
                      date: entry.at ? new Date(entry.at) : new Date(0)
                    })),
                    ...messages.map(msg => ({
                      type: 'message',
                      data: msg,
                      date: msg.createdAt ? new Date(msg.createdAt) : new Date(0)
                    }))
                  ].sort((a, b) => a.date - b.date);

                  if (allEntries.length === 0) {
                    return <div className="text-center text-xs text-gray-500 py-4">No messages yet</div>;
                  }

                  return allEntries.map((item, index) => {
                    const isLast = index === allEntries.length - 1;
                    
                    if (item.type === 'timeline') {
                      const entry = item.data;
                      const isCompleted = entry.type !== 'pending' && entry.type !== 'due_date';
                      const date = entry.at ? new Date(entry.at) : null;
                      const formattedDate = date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                      const formattedTime = date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

                      return (
                        <div key={`timeline-${entry._id || index}`} className="flex gap-3">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-emerald-400' : 'bg-white border-2 border-gray-300'
                            }`}>
                              {isCompleted ? (
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                  <path d="M3.5 7L5.83333 9.33333L10.5 4.66667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                              )}
                            </div>
                            {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-2 mb-2 min-h-[20px]"></div>}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium ${
                                isCompleted ? 'text-emerald-600' : 'text-gray-900'
                              }`}>
                                {entry.title || entry.type}
                              </span>
                              {formattedTime && (
                                <span className="text-xs text-gray-500">{formattedTime}</span>
                              )}
                            </div>
                            {formattedDate && (
                              <p className="text-xs text-gray-500 mb-3">{formattedDate}</p>
                            )}
                            {entry.body && (
                              <div className={`rounded-lg p-3 ${
                                entry.type === 'message' || entry.type === 'request' 
                                  ? 'bg-purple-50 border border-purple-200' 
                                  : 'bg-emerald-50 border border-emerald-200'
                              }`}>
                                <p className="text-xs text-gray-700 mb-2">{entry.body}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      // Message entry
                      const msg = item.data;
                      const date = msg.createdAt ? new Date(msg.createdAt) : null;
                      const formattedDate = date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                      const formattedTime = date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                      const isFromCustomer = !!msg.fromCustomerId && !msg.isInternal;
                      const isFromAdmin = !!msg.fromUserId || msg.isInternal;

                      if (isFromCustomer) {
                        const customerName = typeof msg.fromCustomerId === 'object' 
                          ? `${msg.fromCustomerId.firstName || ''} ${msg.fromCustomerId.lastName || ''}`.trim() || 'Customer'
                          : 'Customer';
                        
                        return (
                          <div key={`msg-${msg._id || index}`} className="flex items-start gap-3 mb-6">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-gray-600">
                                {customerName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-medium text-gray-900">{customerName}</span>
                                <span className="text-xs text-gray-500">{formattedTime}</span>
                              </div>
                              {formattedDate && (
                                <p className="text-xs text-gray-500 mb-2">{formattedDate}</p>
                              )}
                              <div className="bg-white border border-gray-200 rounded-lg p-3">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.body}</p>
                                {msg.attachments && msg.attachments.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {msg.attachments.map((att, attIdx) => (
                                      <a
                                        key={`att-${attIdx}`}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                                      >
                                        📎 {att.filename || 'Download document'}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        // Admin message - RIGHT side
                        const adminName = typeof msg.fromUserId === 'object' 
                          ? `${msg.fromUserId.firstName || ''} ${msg.fromUserId.lastName || ''}`.trim() || 'Admin'
                          : 'Admin';
                        
                        return (
                          <div key={`msg-${msg._id || index}`} className="flex items-start gap-3 mb-6 justify-end">
                            <div className="flex-1 flex flex-col items-end">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">{formattedTime}</span>
                                <span className="text-xs font-medium text-gray-900">{adminName}</span>
                              </div>
                              {formattedDate && (
                                <p className="text-xs text-gray-500 mb-2">{formattedDate}</p>
                              )}
                              <div className="bg-[#41c291] text-white rounded-lg p-3 max-w-[80%]">
                                <p className="text-sm text-white whitespace-pre-wrap">{msg.body}</p>
                                {msg.attachments && msg.attachments.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {msg.attachments.map((att, attIdx) => (
                                      <a
                                        key={`att-${attIdx}`}
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-white underline"
                                      >
                                        📎 {att.filename || 'Download document'}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-[#41c291] flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium text-white">
                                {adminName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    }
                  });
                })()}
              </div>

              {/* Message Input */}
              <div className="border-t border-[#e7ebe9] p-[12px]">
                <div className="flex gap-[8px] items-center">
                  <button className="p-[8px] rounded-[8px] hover:bg-[#F8F9FA] transition-colors">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#73827C" strokeWidth="1.5">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <input
                    type="text"
                    placeholder="Placeholder"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sendingRequest}
                    className="flex-1 px-[12px] py-[8px] bg-white border border-[#ebebeb] rounded-[8px] text-[14px] text-[#73827c] focus:outline-none focus:ring-2 focus:ring-[#40C191] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button 
                    onClick={handleSendMessage}
                    disabled={sendingRequest || !messageText.trim()}
                    className="p-[8px] bg-[#41c291] rounded-[8px] hover:bg-[#35A47C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingRequest ? (
                      <div className="w-[24px] h-[24px] border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                        <path d="M22 2L11 13" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 2L15 22L11 13L2 9L22 2Z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Back to Reviewer Modal */}
      <SendBackToReviewerModal
        isOpen={showSendBackModal}
        onClose={() => setShowSendBackModal(false)}
        onSubmit={handleSendBackToReviewer}
      />

      {/* Add Support Document Modal (quotations) */}
      <AddSupportDocumentModal
        isOpen={isAddDocumentModalOpen}
        onClose={() => setIsAddDocumentModalOpen(false)}
        onAdd={handleAddQuotationDocument}
      />
    </div>
  );
};

export default QuotationDetail;

